'use client';

import Link from 'next/link';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { ClockIcon, CurrencyDollarIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import CardRating from '@components/elements/CardRating';
import { agoraRating } from '@lib/vendor-rating';
import { formatPrice } from '@utils/price';
import { getSeoCurrency } from '@utils/seo-locale';
import { trackEvent } from '../../../lib/analytics/posthog';
import { EVENTS } from '../../../lib/analytics/events';
import { trackGoogleEvent } from '../../../lib/analytics/google';

const getAvatarThumbnailUrl = (avatarUrl) => {
  if (!avatarUrl) return '';

  try {
    const url = new URL(avatarUrl);
    const isAgoraCloudFrontAvatar =
      url.hostname.endsWith('.cloudfront.net') && url.pathname.includes('/users/');
    const alreadyOptimized = /(?:\.webp|_\d+\.webp)$/i.test(url.pathname);

    if (!isAgoraCloudFrontAvatar || alreadyOptimized) return avatarUrl;

    url.pathname = `${url.pathname}_100.webp`;
    return url.toString();
  } catch {
    return avatarUrl;
  }
};

export default function ServiceTypeCard({ vendor }) {
  const displayName = vendor.storefront_name || `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim();
  const profileUrl = `/negocios/${vendor.username}`;
  const bookingUrl = vendor.service_slug
    ? `/negocios/${vendor.username}/servicios/${vendor.service_slug}`
    : profileUrl;
  // Reseñas verificadas (la API solo las manda si el negocio las muestra) y Google: la tarjeta muestra una.
  const agora = agoraRating(vendor.agora_rating, vendor.agora_review_count);
  const googleValue = Number(vendor.google_rating);
  const google =
    googleValue >= 1 && googleValue <= 5
      ? { source: 'google', rating: googleValue, count: Number(vendor.google_review_count) || 0 }
      : null;

  const handleCardClick = () => {
    trackEvent(EVENTS.DISCOVERY_VENDOR_CARD_CLICKED, {
      vendor_username: vendor.username,
      source: 'category',
      card_type: 'service_type'
    });
  };

  return (
    <article className="group relative p-4 rounded-xl border border-gray-200 bg-white/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300 h-full flex flex-col justify-between">
      {/* Header */}
      <header className="w-full mb-3">
        <div className="flex items-center gap-3">
          {vendor.avatar_url && (
            <img
              src={getAvatarThumbnailUrl(vendor.avatar_url)}
              alt={displayName}
              width={40}
              height={40}
              loading="lazy"
              decoding="async"
              onError={(event) => {
                if (event.currentTarget.src !== vendor.avatar_url) {
                  event.currentTarget.src = vendor.avatar_url;
                }
              }}
              className="w-10 h-10 rounded-full object-cover border border-gray-100 flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            <h3 className="text-[13px] font-semibold text-[#1a1a1a] leading-snug group-hover:text-primary-600 transition-colors line-clamp-1">
              <Link href={profileUrl} className="after:absolute after:inset-0" onClick={handleCardClick}>
                {displayName}
              </Link>
            </h3>
            <p className="text-[11px] text-gray-400 line-clamp-1 mt-0.5">{vendor.service_name}</p>
          </div>
        </div>
      </header>

      {/* Calificación (una, con su fuente) + reservas */}
      <div className="w-full flex items-center gap-2 mb-2 flex-wrap">
        <CardRating agora={agora} google={google} />
        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
          <CalendarDaysIcon className="w-3 h-3" />
          Acepta reservas
        </span>
      </div>

      {/* Price & Duration */}
      <div className="w-full flex-1 space-y-2 mb-3">
        {vendor.service_price > 0 && (
          <div className="flex items-center gap-1.5">
            <CurrencyDollarIcon className="w-3 h-3 text-emerald-500" />
            <span className="text-[13px] font-semibold text-[#1a1a1a] tabular-nums">
              {formatPrice(vendor.service_price, getSeoCurrency({ country: vendor.country, explicitCurrency: vendor.currency }))}
            </span>
          </div>
        )}
        {vendor.service_duration > 0 && (
          <div className="flex items-center gap-1.5">
            <ClockIcon className="w-3 h-3 text-gray-400" />
            <span className="text-[11px] text-gray-500">{vendor.service_duration} min</span>
          </div>
        )}
        {vendor.state && (
          <p className="text-[11px] text-gray-400 line-clamp-1">{vendor.address || vendor.state}</p>
        )}
      </div>

      {/* CTA */}
      <footer className="w-full pt-2.5 border-t border-gray-100">
        <Link
          href={bookingUrl}
          onClick={() => {
            trackEvent(EVENTS.VENDOR_CONTACT_CLICKED, {
              vendor_username: vendor.username,
              contact_type: 'booking_link',
              destination: 'vendor_profile',
              source_page: 'category'
            });
            trackGoogleEvent('generate_lead', {
              lead_source: 'marketplace_booking',
              vendor_username: vendor.username,
              service_name: vendor.service_name
            });
          }}
          className="relative z-10 inline-flex items-center gap-1 text-[12px] font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          Reservar turno
          <ArrowRightIcon className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </footer>
    </article>
  );
}
